import React, { JSXElementConstructor, useContext } from "react";
import { GroupAddIcon } from "@ui/icons/material/GroupAdd";
import { PeopleIcon } from "@ui/icons/material/People";
import { FileDownloadDoneIcon } from "@ui/icons/material/FileDownloadDone";
import {
  DatabaseNotification,
  DatabaseNotificationAction,
} from "./database-notification";
import { MixedImage } from "@ui/images/mixed-image";
import { Button } from "@ui/buttons/button";
import { SiteConfigContext } from "@common/core/settings/site-config-context";
import { Line } from "@common/notifications/notification-line";
import clsx from "clsx";
import { useMarkNotificationsAsRead } from "@common/notifications/requests/use-mark-notifications-as-read";
import { useNavigate } from "@common/ui/navigation/use-navigate";
import { isAbsoluteUrl } from "@ui/utils/urls/is-absolute-url";
import { isLocalUrl } from "@ui/utils/urls/is-local-url";
import { Link } from "react-router-dom";
import { useSettings } from "@ui/settings/use-settings";
import { SvgIconProps } from "@ui/icons/svg-icon";
import { getBootstrapData } from "@ui/bootstrap-data/bootstrap-data-store";
import { FormattedRelativeTime } from "@ui/i18n/formatted-relative-time";
import { Trans } from "@ui/i18n/trans";
import { NotificationActivationPanel } from "./notification-activation-panel";
import { isRenderedAsMobileApp } from "@common/core/platform-detection";

const iconMap = {
  "group-add": GroupAddIcon,
  people: PeopleIcon,
  "export-csv": FileDownloadDoneIcon,
} as Record<string, JSXElementConstructor<SvgIconProps>>;

interface NotificationListProps {
  notifications: DatabaseNotification[];
  className?: string;
  onNotificationClick?: () => void;
}
export function NotificationList({
  notifications,
  className,
  onNotificationClick,
}: NotificationListProps) {
  const { notifications: config } = useContext(SiteConfigContext);
  const isNativeMobile = isRenderedAsMobileApp();
  return (
    <div
      className={clsx(
        className,
        isNativeMobile && "app-ambient-notification-list",
      )}
    >
      <NotificationActivationPanel />
      {notifications.map((notification, index) => {
        const isLast = notifications.length - 1 === index;
        const Renderer =
          config?.renderMap?.[notification.type] || NotificationListItem;
        return (
          <Renderer
            key={notification.id}
            notification={notification}
            isLast={isLast}
            onNotificationClick={onNotificationClick}
          />
        );
      })}
    </div>
  );
}

export interface NotificationListItemProps {
  notification: DatabaseNotification;
  onActionButtonClick?: ButtonActionsProps["onActionClick"];
  lineImageRenderer?: JSXElementConstructor<{ image: string }>;
  isLast: boolean;
  onNotificationClick?: () => void;
}
export function NotificationListItem({
  notification,
  onActionButtonClick,
  lineImageRenderer,
  isLast,
  onNotificationClick,
}: NotificationListItemProps) {
  const markAsRead = useMarkNotificationsAsRead();
  const navigate = useNavigate();
  const mainAction = notification.data.mainAction;
  const action = notification.data.action;
  const showUnreadIndicator = !notification.data.image && !notification.read_at;
  const isNativeMobile = isRenderedAsMobileApp();

  return (
    <div
      onClick={() => {
        if (!markAsRead.isPending && !notification.read_at) {
          markAsRead.mutate({ ids: [notification.id] });
        }
        if (mainAction?.action) {
          if (
            isAbsoluteUrl(mainAction.action) &&
            !isLocalUrl(mainAction.action)
          ) {
            window.open(mainAction.action, "_blank")?.focus();
          } else {
            navigate(
              mainAction.action.replace(
                `${getBootstrapData().settings.base_url}/`,
                "/",
              ),
            );
          }
        } else if (action) {
          if (isAbsoluteUrl(action.link) && !isLocalUrl(action.link)) {
            window.open(action.link, "_blank")?.focus();
          } else {
            navigate(
              action.link.replace(
                `${getBootstrapData().settings.base_url}/`,
                "/",
              ),
            );
          }
        }
        onNotificationClick ? onNotificationClick() : null;
      }}
      className={clsx(
        "relative flex items-start gap-14 px-32 py-20",
        isNativeMobile ? "app-ambient-notification-item" : "bg-alt",
        !isLast && "border-b",
        (mainAction?.action || action) && "cursor-pointer",
        !notification.read_at
          ? isNativeMobile
            ? "app-ambient-notification-item-unread"
            : "bg-paper hover:bg-primary/10"
          : isNativeMobile
          ? "app-ambient-notification-item-read"
          : "hover:bg-hover",
      )}
      title={
        mainAction?.label
          ? mainAction.label
          : action?.label
          ? action.label
          : undefined
      }
    >
      {showUnreadIndicator && (
        <div className="absolute left-16 top-26 h-8 w-8 flex-shrink-0 rounded-full bg-primary shadow" />
      )}
      <div className="min-w-0" style={{ width: "100%" }}>
        <div className="flex items-start gap-8">
          {notification.data.icon && (
            <MixedImage
              className="rounded-panel shadow-md h-50 text-muted"
              src={iconMap[notification.data.icon] || notification.data.icon}
            />
          )}
          <div className="">
            {notification.data.title && (
              <div className="font-bold">
                <Trans
                  message={notification.data.title}
                  values={notification.data.i18n}
                />
              </div>
            )}
            <time className="text-xs text-muted">
              <FormattedRelativeTime date={notification.created_at} />
            </time>
          </div>
        </div>
        {notification.data.lines.map((line, index) => (
          <Line
            imageRenderer={lineImageRenderer}
            notification={notification}
            line={line}
            index={index}
            key={index}
          />
        ))}
        <ButtonActions
          onActionClick={onActionButtonClick}
          notification={notification}
        />
      </div>
    </div>
  );
}

interface ButtonActionsProps {
  notification: DatabaseNotification;
  onActionClick?: (
    e: React.MouseEvent,
    action: DatabaseNotificationAction,
  ) => void;
}
function ButtonActions({ notification, onActionClick }: ButtonActionsProps) {
  const { base_url } = useSettings();
  if (!notification.data.buttonActions) return null;

  return (
    <div className="mt-12 flex items-center gap-12">
      {notification.data.buttonActions.map((action, index) => (
        <Button
          key={index}
          size="xs"
          variant={index === 0 ? "flat" : "outline"}
          color={index === 0 ? "primary" : null}
          elementType={!onActionClick ? Link : undefined}
          to={!onActionClick ? action.action.replace(base_url, "") : undefined}
          onClick={(e) => {
            onActionClick?.(e, action);
          }}
        >
          {action.label}
        </Button>
      ))}
    </div>
  );
}
